import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, Button, LinearProgress, Chip, Stack } from '@mui/material';
import HubIcon from '@mui/icons-material/Hub';
import VerifiedIcon from '@mui/icons-material/Verified';
import { REGISTRY_COLORS, colorForRegistry as colorFor } from '../theme/registryColors';

const GREEN = '#76B900';

// Theatrical loader for the "Load member publishers" step: streams publishers in
// from the Trust Graph with live per-registry counts. Pure client-side animation
// over already-fetched data — the DB load itself is instant.
export default function TrustGraphLoader({ members, orgName, onDone }) {
  const total = members.length;
  const [phase, setPhase] = useState('connecting'); // connecting -> streaming -> done
  const [revealed, setRevealed] = useState(0);
  const timer = useRef(null);

  // Precompute cumulative registry counts so each tick is O(1).
  const cumulative = useMemo(() => {
    const running = {};
    return members.map((m) => {
      (m.associations || []).forEach((a) => { running[a.short] = (running[a.short] || 0) + 1; });
      return { ...running };
    });
  }, [members]);

  useEffect(() => {
    const t = setTimeout(() => setPhase('streaming'), 850);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (phase !== 'streaming') return;
    const chunk = Math.max(3, Math.round(total / 130));
    const tick = () => {
      setRevealed((n) => {
        const next = Math.min(total, n + chunk);
        if (next >= total) { setPhase('done'); return total; }
        timer.current = setTimeout(tick, 26);
        return next;
      });
    };
    timer.current = setTimeout(tick, 26);
    return () => clearTimeout(timer.current);
  }, [phase, total]);

  const counts = revealed > 0 ? cumulative[revealed - 1] : {};
  const registries = Object.keys(REGISTRY_COLORS).filter((r) => counts[r]);
  const feed = members.slice(Math.max(0, revealed - 9), revealed).reverse();
  const pct = total ? Math.round((revealed / total) * 100) : 0;

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'radial-gradient(1200px 700px at 50% -10%, #12202a 0%, #070a0e 65%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      p: 3, color: '#e6edf3',
      '@keyframes tgpulse': { '0%,100%': { transform: 'scale(1)', opacity: 0.85 }, '50%': { transform: 'scale(1.12)', opacity: 1 } },
      '@keyframes tgring': { '0%': { transform: 'scale(0.8)', opacity: 0.7 }, '100%': { transform: 'scale(2.2)', opacity: 0 } },
      '@keyframes tgin': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
    }}>
      {/* Trust Graph node */}
      <Box sx={{ position: 'relative', width: 96, height: 96, mb: 2 }}>
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{
            position: 'absolute', inset: 0, borderRadius: '50%', border: `2px solid ${GREEN}`,
            animation: 'tgring 2s ease-out infinite', animationDelay: `${i * 0.6}s`,
          }} />
        ))}
        <Box sx={{
          position: 'absolute', inset: 18, borderRadius: '50%',
          background: `radial-gradient(circle, ${GREEN} 0%, #4d7a00 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'tgpulse 1.6s ease-in-out infinite', boxShadow: `0 0 40px ${GREEN}66`,
        }}>
          <HubIcon sx={{ fontSize: 30, color: '#fff' }} />
        </Box>
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {phase === 'done' ? 'Member publishers loaded' : 'Querying the Trust Graph…'}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.6, mb: 2 }}>
        {orgName || 'Your organization'} · From the Noosphere Trust Graph · cross-registry associations
      </Typography>

      {/* Big count */}
      <Typography sx={{ fontSize: 64, fontWeight: 800, lineHeight: 1, color: GREEN, fontVariantNumeric: 'tabular-nums' }}>
        {revealed.toLocaleString()}
      </Typography>
      <Typography variant="overline" sx={{ opacity: 0.6, letterSpacing: 2 }}>member publishers</Typography>

      {/* Registry counters */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center" sx={{ mt: 2, maxWidth: 620 }}>
        {registries.map((r) => (
          <Chip key={r} size="small" label={`${r} · ${counts[r].toLocaleString()}`}
            sx={{ bgcolor: `${colorFor(r)}22`, color: colorFor(r), border: `1px solid ${colorFor(r)}66`, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }} />
        ))}
      </Stack>

      {/* Progress */}
      <Box sx={{ width: 'min(620px, 90vw)', mt: 3 }}>
        <LinearProgress variant="determinate" value={pct}
          sx={{ height: 8, borderRadius: 4, bgcolor: '#ffffff14', '& .MuiLinearProgress-bar': { bgcolor: GREEN } }} />
      </Box>

      {/* Live feed */}
      <Box sx={{ width: 'min(620px, 90vw)', height: 160, mt: 2, overflow: 'hidden' }}>
        {feed.map((m) => (
          <Box key={m.domain} sx={{
            display: 'flex', alignItems: 'center', gap: 1, py: 0.5,
            animation: 'tgin 0.28s ease-out',
          }}>
            <VerifiedIcon sx={{ fontSize: 16, color: GREEN, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
              {m.name || m.domain}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {(m.associations || []).slice(0, 4).map((a) => (
                <Box key={a.domain} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colorFor(a.short) }} />
              ))}
            </Stack>
          </Box>
        ))}
      </Box>

      {phase === 'done' && (
        <Button variant="contained" size="large" onClick={onDone}
          sx={{ mt: 2, bgcolor: GREEN, '&:hover': { bgcolor: '#5c8f00' }, fontWeight: 700, px: 4 }}>
          Continue — {total.toLocaleString()} publishers ready
        </Button>
      )}
    </Box>
  );
}
